import { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import { useLanguage } from '../context/LanguageContext';

/**
 * useContent — Resolve platform content by key from the Legal Content Engine.
 *
 * Reads language from LanguageContext. Calls GET /api/public/content/resolve.
 * Returns { loading, error, content, refetch }.
 * NO hardcoded fallback. If API fails → error state.
 */
export function useContent(contentKey, { flow, country } = {}) {
  const { language } = useLanguage();
  const [state, setState] = useState({ loading: true, error: null, content: null });

  const fetchContent = useCallback(async () => {
    if (!contentKey) {
      setState({ loading: false, error: 'No content key provided', content: null });
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const params = new URLSearchParams({ key: contentKey, language });
      if (country) params.append('country', country);
      if (flow) params.append('flow', flow);

      const { data } = await api.get(`/public/content/resolve?${params.toString()}`);

      setState({
        loading: false,
        error: null,
        content: {
          contentKey: data.content_key,
          originalKey: data.original_key,
          title: data.title,
          body: data.body,
          summary: data.summary,
          acceptanceLabel: data.acceptance_label,
          blockingFlows: data.blocking_flows,
          version: data.version,
          requiresAcceptance: data.requires_acceptance,
          acceptanceMechanism: data.acceptance_mechanism,
          isBlocking: data.is_blocking,
          contentType: data.content_type,
          scope: data.scope,
          isFallbackLanguage: data.is_fallback_language,
          resolvedLanguage: data.resolved_language,
          effectiveFrom: data.effective_from,
          publishedAt: data.published_at,
          updatedAt: data.updated_at,
          changelog: data.changelog,
        },
      });
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Failed to load content';
      setState({ loading: false, error: message, content: null });
    }
  }, [contentKey, language, country, flow]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  return { ...state, refetch: fetchContent };
}
